/**
 * Runtime mode detection — TV vs phone vs desktop.
 *
 * The same SPA bundle runs everywhere; certain views (StreamPlayer,
 * StreamDashboard chrome, ChannelGrid keyboard handlers) want to know
 * if they're rendering on an Android TV / Fire TV so they can:
 *   - Default to full-screen player
 *   - Show focus rings prominently
 *   - Honor D-pad keys (up/down/left/right/Enter/Back)
 *   - Hide management chrome that doesn't make sense at 10ft
 *
 * Detection precedence (first wins):
 *   1. `?tv=1` query param — manual override, most reliable for testing
 *   2. localStorage `streamflow.tvMode === 'true'` — sticky per-device
 *      (set by the user via a settings toggle, or auto-set on first
 *      successful TV pairing redeem)
 *   3. UA-based: navigator.userAgent matching one of the TV signatures
 *      Android TV's WebView UA contains "TV" or the OEM name (BRAVIA,
 *      AFTM, etc.). Fire TV UA contains "AFTS"/"AFTM". Any of these →
 *      TV mode.
 *
 * Memoized at module load — call sites can use the value freely without
 * worrying about cost. If the URL changes after first call (e.g. SPA
 * navigation), use `forceTvMode()` / `clearTvMode()` to update.
 */

const STORAGE_KEY = 'streamflow.tvMode';

let _cached: boolean | null = null;

function detectFromUrl(): boolean | null {
  if (typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('tv') === '1') return true;
    if (params.get('tv') === '0') return false;
  } catch {
    /* ignore */
  }
  return null;
}

function detectFromStorage(): boolean | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'true') return true;
    if (v === 'false') return false;
  } catch {
    /* ignore */
  }
  return null;
}

function detectFromUserAgent(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  // Common TV-platform UA tokens. Liberal — false positives just give
  // the user the more-readable, D-pad-friendly UI on a phone, which is
  // not really wrong.
  if (/\b(?:TV|AFTM|AFTS|AFTB|AFTR|BRAVIA|GoogleTV|SHIELD|AndroidTV|crKey)\b/.test(ua)) {
    return true;
  }
  // Fallback heuristic: coarse pointer + TV-sized screen. Android TV
  // boxes from no-name OEMs sometimes ship without the distinctive UA
  // tokens above. Phones have coarse pointers but viewport <1280; desktops
  // have fine pointers, so this fires only for actual TVs.
  try {
    if (
      typeof window !== 'undefined' &&
      window.matchMedia?.('(pointer: coarse)').matches &&
      window.screen?.width >= 1280
    ) {
      return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

export function isTvMode(): boolean {
  if (_cached !== null) return _cached;
  const url = detectFromUrl();
  if (url !== null) {
    _cached = url;
    return url;
  }
  const stored = detectFromStorage();
  if (stored !== null) {
    _cached = stored;
    return stored;
  }
  _cached = detectFromUserAgent();
  return _cached;
}

export function forceTvMode(on: boolean): void {
  _cached = on;
  try {
    localStorage.setItem(STORAGE_KEY, on ? 'true' : 'false');
  } catch {
    /* ignore */
  }
}

export function clearTvMode(): void {
  _cached = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
